/**
 * Copyright (c) 2023 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { PreflightTestReport } from 'twilio-video';

export enum PreflightQualityScore {
  Poor,
  Suboptimal,
  Good,
  Excellent,
}

export function getPreflightQualityScore(preflightTestReport: PreflightTestReport): PreflightQualityScore {
  const { stats } = preflightTestReport;

  const latencyScore = getSingleQualityScore(stats.rtt?.average, 100, 250, 400);

  const jitterScore = getSingleQualityScore(stats.jitter?.average, 5, 10, 30);

  const packetLossScore = getSingleQualityScore(stats.packetLoss?.average, 1, 3, 8);

  return Math.min(latencyScore, jitterScore, packetLossScore) as PreflightQualityScore;
}

// Calculate Network Quality Score based on download speed and latency (for Zoom)
export function calculateNetworkQualityScore(downloadSpeed: number, latency: number): PreflightQualityScore {
  const downloadScore = getSingleQualityScore(downloadSpeed, 3.0, 1.2, 0.5, true); // Mbps thresholds
  const latencyScore = getSingleQualityScore(latency, 135, 285, 450); // ms thresholds

  // Overall score is the worst of the two
  return Math.min(downloadScore, latencyScore) as PreflightQualityScore;
}

function getSingleQualityScore(
  stat: number | undefined,
  goodThreshold: number,
  suboptimalThreshold: number,
  poorThreshold: number,
  descending = false,
) {
  if (typeof stat === 'undefined') {
    return PreflightQualityScore.Poor;
  }

  if (descending) {
    if (stat > goodThreshold) {
      return PreflightQualityScore.Excellent;
    }
    if (stat > suboptimalThreshold) {
      return PreflightQualityScore.Good;
    }
    if (stat > poorThreshold) {
      return PreflightQualityScore.Suboptimal;
    }
    return PreflightQualityScore.Poor;
  }

  if (stat >= poorThreshold) {
    return PreflightQualityScore.Poor;
  }
  if (stat >= suboptimalThreshold) {
    return PreflightQualityScore.Suboptimal;
  }
  if (stat >= goodThreshold) {
    return PreflightQualityScore.Good;
  }
  return PreflightQualityScore.Excellent;
}
